import type { BoxProps, ChakraProps } from '@chakra-ui/react';
import { Box, Flex, Text, useBreakpointValue } from '@chakra-ui/react';
import { SlideIndicator } from '@ui/components/SlideIndicator';
import { EXTERNAL_LINK } from '@ui/config/externalLinks';
import { pathDict } from '@ui/config/paths';
import { isFeatureEnabled } from '@ui/hooks/useIsFeatureEnabled';
import { useVnoUsdValue } from '@ui/hooks/useVnoUsdValue';
import { useTranslations } from '@ui/i18n';
import type { ImageProps as NextImageProps } from 'next/image';
import React, { useEffect, useMemo, useState } from 'react';
import { Autoplay } from 'swiper';
import type { Swiper as SwiperClass } from 'swiper/types';

import { features } from '../Features';
import {
  InDesktop,
  InMobile,
  RenderInDesktop,
  RenderInMobile,
} from '../MobileOrDesktop';
import { SwiperSlide, SwiperWithScale } from '../Swiper';
import { TokenIcon } from '../TokenIcon';
import type { UpdateBannerProps } from './UpdateBanner';
import UpdateBanner from './UpdateBanner';
import UpdateBannerDesktop from './UpdateBannerDesktop';

type UpdateBanner = Omit<UpdateBannerProps, 'onClose'> & {
  id: string;
  mainImgPropsDesktop?: Omit<NextImageProps, 'src'>;
};

const useBanners = () => {
  const t = useTranslations();

  return useMemo(() => {
    const result: UpdateBanner[] = [];
    const banners: (UpdateBanner | null)[] = [
      isFeatureEnabled(features.tectonicSupportVno)
        ? {
            id: 'tectonic-veno',
            mainImg: '/images/tectonic-logo.png',
            mainImgProps: { width: '160px', height: '23px' },
            mainImgPropsDesktop: { width: '150px', height: '20px' },
            isLinkExternal: true,
            link: 'https://app.tectonic.finance/markets/defi',
            text: t('{platform} now lists {coin}!', {
              platform: 'Tectonic',
              coin: 'VNO',
            }),
            secondaryText: t(
              "Supply and borrow {coin} on Cronos' leading Money Market",
              {
                coin: 'VNO',
              },
            ),
            bgImage: '/images/pattern-4-bg.png',
            backgroundSize: '236px 116px',
            linkText: (
              <>
                <InMobile>{t('Supply')}</InMobile>
                <InDesktop>
                  {t('Supply {coin}', {
                    coin: 'VNO',
                  })}
                </InDesktop>
              </>
            ),
          }
        : null,
      {
        id: 'tectonic-latom',
        mainImg: '/images/tectonic-logo.png',
        mainImgProps: { width: '160px', height: '23px' },
        mainImgPropsDesktop: { width: '150px', height: '20px' },
        isLinkExternal: true,
        link: 'https://app.tectonic.finance/markets/veno',
        text: t('{platform} now lists {coin}!', {
          platform: 'Tectonic',
          coin: 'LATOM',
        }),
        secondaryText: t('Borrow against your supplied {coin} on {platform}', {
          coin: 'LATOM',
          platform: 'Tectonic',
        }),
        bgImage: '/images/pattern-4-bg.png',
        backgroundSize: '236px 116px',
        linkText: (
          <>
            <InMobile>{t('Supply')}</InMobile>
            <InDesktop>
              {t('Supply {coin}', {
                coin: 'LATOM',
              })}
            </InDesktop>
          </>
        ),
      },
      {
        id: 'vaults',
        mainImg: '/images/text-logo.png',
        mainImgProps: { width: '120px', height: '29px' },
        isLinkExternal: false,
        link: '/vno',
        text: t('Earn VNO with the Garden'),
        secondaryText: `🌱${t('Plant')}. 💧 ${t('Grow')}. 💲${t('Harvest')}.`,
        bgImage: '/images/pattern-4-bg.png',
        backgroundSize: '236px 116px',
        linkText: t('Earn now'),
      },
      {
        id: 'vvs',
        mainImg: '/images/vvs-logo.png',
        mainImgProps: { width: '201px', height: '28px' },
        mainImgPropsDesktop: { width: '190', height: '26px' },
        isLinkExternal: true,
        link: EXTERNAL_LINK.vvs,
        text: t('VNO now on VVS!'),
        secondaryText: '1 VNO = {vnoPrice} USD',
        bgImage: '/images/pattern-4-bg.png',
        backgroundSize: '236px 116px',
        linkText: t('Buy now'),
      },
      {
        id: 'atom',
        mainImg: (
          <Flex alignItems="center" gap="4px">
            <TokenIcon token="latom" w="34px" h="34px" />
            <Text>LATOM</Text>
          </Flex>
        ),
        isLinkExternal: false,
        link: pathDict.atom.stake,
        text: (
          <Box>
            {t('Over 21% Staking APY')}{' '}
            <TokenIcon
              display="inline-block"
              token="atom"
              verticalAlign="bottom"
            />
          </Box>
        ),
        secondaryText: `${t('Plus additional DeFi rewards')} 💲`,
        bgImage: '/images/pattern-4-bg.png',
        backgroundSize: '236px 116px',
        linkText: t('Stake ATOM'),
      },
    ];

    for (const item of banners) {
      if (item) {
        result.push(item);
      }
    }

    return result;
  }, [t]);
};

type UpdateBannersProps = BoxProps;

function UpdateBanners({ ...boxProps }: UpdateBannersProps) {
  const banners = useBanners();
  const [swiper, setSwiper] = useState<SwiperClass | null>(null);
  const [shownBanners, setShownBanners] = useState<UpdateBanner[]>([]);
  const [selectedSlide, setSelectedSlide] = useState(0);
  const { data: vnoPrice } = useVnoUsdValue(1);

  const slidesPerView = useBreakpointValue({ base: 1, desktop: 1 });
  const swiperPosition: ChakraProps['position'] = {
    base: 'absolute',
    desktop: 'static',
  };

  const hideBanner = (id: string) => {
    setShownBanners((shownBanners) => shownBanners.filter((b) => b.id !== id));
    localStorage.setItem(`${id}-banner-hidden`, 'true');
  };

  useEffect(() => {
    const shownBanners = banners
      .filter((b) => 'true' !== localStorage.getItem(`${b.id}-banner-hidden`))
      .map((sb) => ({
        ...sb,
        secondaryText: sb.secondaryText.replace(
          '{vnoPrice}',
          (vnoPrice || 0)?.toFixed(2),
        ),
      }));
    setShownBanners(shownBanners);
  }, [banners, vnoPrice]);

  return (
    <Box
      position="relative"
      zIndex={1}
      maxW="920px"
      mx="auto"
      {...boxProps}
      h={shownBanners.length ? boxProps.h : 0}
    >
      <SwiperWithScale
        autoplay={{ delay: 5000, disableOnInteraction: true }}
        position={swiperPosition}
        scaleEnabled={false}
        grabCursor={false}
        modules={[Autoplay]}
        slidesPerView={slidesPerView}
        onSlideChange={({ realIndex }) => {
          setSelectedSlide(realIndex);
        }}
        onSwiper={setSwiper}
        h={shownBanners.length ? '146px' : 0}
        cursor={shownBanners.length >= 2 ? 'grab' : 'auto'}
      >
        {shownBanners.map((sb) => (
          <SwiperSlide key={sb.id}>
            <RenderInMobile>
              <UpdateBanner onClose={() => hideBanner(sb.id)} {...sb} />
            </RenderInMobile>
            <RenderInDesktop>
              <UpdateBannerDesktop
                onClose={() => hideBanner(sb.id)}
                {...sb}
                mainImgProps={sb.mainImgPropsDesktop ?? sb.mainImgProps}
              />
            </RenderInDesktop>
          </SwiperSlide>
        ))}
      </SwiperWithScale>
      <SlideIndicator
        total={shownBanners.length}
        index={selectedSlide}
        onChange={(index) => {
          swiper?.slideTo(index);
        }}
        // occupy the place to avoid UI flickering
        opacity={shownBanners.length >= 2 ? 1 : 0}
        position={swiperPosition}
        top="144px"
        left="0"
        right="0"
      />
    </Box>
  );
}

export default UpdateBanners;
